import type { LineI } from "tweenly"
import { useMemo } from "react"
import { LINE_HEIGHT, renderLineSvg } from "tweenly"
import AbstractContainer from "./AbstractContainer"

/**
 * Screen px the line can be picked up from on each side of its stroke — a hairline would
 * otherwise have to be hit exactly, which Figma spares you as well.
 */
const LINE_HIT_MARGIN_PX = 5

interface Props {
    object: LineI
    isHiding: boolean
    isLocking: boolean
    isRootTicker: boolean
    canvasScaleCoef: number
    borderCoef: number
    handleSelectObject: (
        event: React.MouseEvent,
        objectId: string,
        animationIds: string[],
        multiSelectKey: boolean
    ) => void
}

/**
 * The object's element is the line's box — as long as the line and as tall as its weight;
 * the SVG inside draws the stroke and the end points. The same markup the HTML export uses
 * (tweenly's renderLineSvg), built from known end types and the object id only.
 */
const LineContainer = ({
    object,
    isHiding,
    isLocking,
    isRootTicker,
    canvasScaleCoef,
    borderCoef,
    handleSelectObject,
}: Props) => {
    // memoised so React only rewrites the SVG when an end point actually changes
    const svg = useMemo(
        () => ({
            __html: renderLineSvg({
                id: object.id,
                startPoint: object.startPoint,
                endPoint: object.endPoint,
            }),
        }),
        [object.id, object.startPoint, object.endPoint]
    )

    // borderCoef is 2 screen px in canvas units, see Canvas
    const hitMargin = (LINE_HIT_MARGIN_PX * borderCoef) / 2

    return (
        <AbstractContainer
            object={object}
            isHiding={isHiding}
            isLocking={isLocking}
            isRootTicker={isRootTicker}
            canvasScaleCoef={canvasScaleCoef}
            borderCoef={borderCoef}
            handleSelectObject={handleSelectObject}
            className={"line-container"}
            visibility={object.isHidden || isHiding ? "hidden" : "visible"}
            style={{ height: LINE_HEIGHT }}
        >
            <div style={{ position: "relative", width: "100%", height: "100%" }}>
                <div style={{ width: "100%", height: "100%" }} dangerouslySetInnerHTML={svg} />
                {/* invisible, but part of the line: a press on it selects and moves the line */}
                <div
                    style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: -hitMargin,
                        bottom: -hitMargin,
                    }}
                />
            </div>
        </AbstractContainer>
    )
}

export default LineContainer
